"use client";

import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { ChevronDown, Search, X } from "lucide-react";

export interface SelectOption {
  value: string;
  label: string;
  flag?: string;
}

export interface SearchableSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  hasError?: boolean;
  searchable?: boolean;
  id?: string;
  /** `light` — white trigger (default). `dark` — translucent trigger for dark form backgrounds. */
  variant?: "light" | "dark";
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  disabled = false,
  hasError = false,
  searchable = true,
  id,
  variant = "light",
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && searchable) setTimeout(() => searchRef.current?.focus(), 60);
  }, [open, searchable]);

  const handleSelect = useCallback(
    (opt: SelectOption) => {
      onChange(opt.value);
      setOpen(false);
      setQuery("");
    },
    [onChange]
  );

  const isDark = variant === "dark";

  const borderClass = hasError
    ? "border-red-400 focus-within:ring-red-100"
    : open
      ? "border-[#2BA6E6] ring-2 ring-blue-50"
      : isDark
        ? "border-[rgba(255,255,255,0.12)] focus-within:border-[#2BA6E6] focus-within:ring-2 focus-within:ring-blue-50/20"
        : "border-neutral-200 focus-within:border-[#2BA6E6] focus-within:ring-2 focus-within:ring-blue-50";

  const triggerClass = isDark
    ? "bg-[rgba(255,255,255,0.06)] hover:bg-[rgba(255,255,255,0.06)] active:bg-[rgba(255,255,255,0.06)]"
    : "bg-white hover:bg-white active:bg-white";

  const textClass = selected
    ? isDark
      ? "text-white"
      : "text-neutral-900"
    : isDark
      ? "text-white/70"
      : "text-neutral-500";

  const chevronClass = isDark ? "text-white/70" : "text-neutral-500";

  return (
    <div ref={containerRef} className="relative">
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border text-sm outline-none transition-[border-color,box-shadow] duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${triggerClass} ${borderClass}`}
      >
        <span className={`flex items-center gap-2 truncate ${textClass}`}>
          {selected ? (
            <>
              {selected.flag && (
                <span className="text-base leading-none flex-shrink-0">{selected.flag}</span>
              )}
              <span className="truncate">{selected.label}</span>
            </>
          ) : (
            placeholder
          )}
        </span>
        <ChevronDown
          className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${chevronClass} ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute z-50 mt-1.5 w-full bg-white border border-neutral-200 rounded-2xl shadow-xl overflow-hidden">
          {searchable && (
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-neutral-100">
              <Search className="w-4 h-4 text-neutral-400 flex-shrink-0" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="flex-1 text-sm !text-neutral-900 placeholder-neutral-400 outline-none bg-transparent border-0 p-0 w-auto"
              />
              {query && (
                <button type="button" onClick={() => setQuery("")}>
                  <X className="w-3.5 h-3.5 text-neutral-400 hover:text-neutral-600" />
                </button>
              )}
            </div>
          )}

          <ul className={`max-h-52 overflow-y-auto ${searchable ? "py-1" : "py-1.5"}`}>
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-neutral-400 text-center">No results found</li>
            ) : (
              filtered.map((opt) => (
                <li key={opt.value}>
                  <button
                    type="button"
                    onClick={() => handleSelect(opt)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors hover:bg-blue-50 ${
                      opt.value === value ? "bg-blue-50 font-semibold text-[#2BA6E6]" : "text-neutral-800"
                    }`}
                  >
                    {opt.flag && (
                      <span className="text-base leading-none flex-shrink-0">{opt.flag}</span>
                    )}
                    {opt.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
